



















































import type { PropType } from '@vue/composition-api';
import { onMounted, computed, defineComponent } from '@vue/composition-api';
import ThemedButton from '@getaccept/lib-shared/src/components/ThemedButton.vue';
import { RecipientStatus } from '@getaccept/lib-shared/src/recipients/enums/recipient-status';
import { RecipientRole } from '@getaccept/lib-shared/src/recipients/enums/recipient-role';
import { SigningHelper } from '@getaccept/lib-shared/src/signature/helpers/signing.helper';
import { t } from '@getaccept/lib-shared/src/helpers/translation.helper';
import type { SigningTheme } from '@getaccept/lib-shared/src/signing-theme/types/theme';
import { DocumentStatus } from '@getaccept/lib-shared/src/enums/document-status';
import type { SigningPageRecipient } from '@getaccept/lib-shared/src/types/signing-page-recipient';
import type { SigningPageDocument } from '@getaccept/lib-shared/src/types/signing-page-document';

export default defineComponent({
  components: {
    ThemedButton,
  },
  props: {
    document: {
      type: Object as PropType<SigningPageDocument>,
      required: true,
    },
    recipient: {
      type: Object as PropType<SigningPageRecipient>,
      required: true,
    },
    requiredIds: {
      type: Array as PropType<string[]>,
      default: () => [],
    },
    nextRequiredId: {
      type: String,
      default: '',
    },
    focusedRequiredId: {
      type: String,
      default: '',
    },
    theme: {
      type: Object as PropType<SigningTheme>,
      required: true,
    },
    isSigningEnabled: {
      type: Boolean,
      required: true,
    },
    startEid: {
      type: Boolean,
      required: true,
    },
    loadingRecipientInput: {
      type: Boolean,
      default: false,
    },
    hasFormInputFieldsWithErrors: {
      type: Boolean,
    },
  },

  setup(props, { emit }) {
    const recipientIsSigner = computed(() => props.recipient.role === RecipientRole.Signer);

    const showRequiredSteps = computed(
      () =>
        (!hasRecipientSigned.value && hasRequiredSteps.value) || props.hasFormInputFieldsWithErrors
    );

    const hasRecipientSigned = computed(() =>
      SigningHelper.hasRecipientSigned(props.recipient.status)
    );

    const hasRequiredSteps = computed(() => props.requiredIds.length > 0 && !!props.nextRequiredId);

    const requiredStepsButtonText = computed(() =>
      props.focusedRequiredId ? t('next') : t('show-required')
    );

    const requiredStepsMessage = computed(() =>
      props.requiredIds.length === 1 ? t('you-have-a-required-step') : t('you-have-required-steps')
    );

    const showSignButton = computed(
      () =>
        [RecipientStatus.Viewed, RecipientStatus.Reviewed].includes(props.recipient.status) &&
        props.isSigningEnabled
    );

    const signButtonText = computed(() => {
      if (recipientIsSigner.value) {
        return t(props.theme?.signButtonText) || t('sign-document');
      }
      return t('approve-document');
    });

    const message = computed(() => {
      if (props.recipient.role === RecipientRole.CC) {
        return '';
      }
      if (props.document.status === DocumentStatus.Signed) {
        return t('document-is-already-signed');
      }
      if (props.recipient.status === RecipientStatus.Rejected) {
        return t('document-has-been-rejected');
      }
      if (recipientIsSigner.value && hasRecipientSigned.value) {
        return t('has-already-signed-document');
      }
      if (
        [RecipientRole.Approver, RecipientRole.ExternalApprover].includes(props.recipient.role) &&
        hasRecipientSigned.value
      ) {
        return t('has-already-approved');
      }
      if (!props.isSigningEnabled) {
        return t('waiting-for-other-signers');
      }
      return '';
    });

    const tooltip = computed(() => {
      if (props.loadingRecipientInput) {
        return t('loading-recipient-input-signing-disabled');
      } else {
        return null;
      }
    });

    const showRequiredStep = (nextRequiredId: string) => {
      if (!nextRequiredId) {
        return;
      }

      const nextElement: HTMLElement = document.getElementById(props.nextRequiredId);
      if (!nextElement) {
        return;
      }
      nextElement.scrollIntoView({ behavior: 'smooth', block: 'center' });
      emit('updateFocusedRequiredId', props.nextRequiredId);
    };

    const sign = () => {
      recipientIsSigner.value ? emit('sign') : emit('approve');
    };

    onMounted(() => {
      if (props.startEid) {
        emit('sign');
      }
    });

    return {
      DocumentStatus,
      RecipientStatus,
      RecipientRole,
      recipientIsSigner,
      showRequiredSteps,
      hasRecipientSigned,
      hasRequiredSteps,
      requiredStepsButtonText,
      requiredStepsMessage,
      showSignButton,
      signButtonText,
      message,
      tooltip,
      showRequiredStep,
      sign,
    };
  },
});
